import { Col, Row, Dropdown, Button, Grid, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import AppTable from '../../../../components/AppTable/AppTable'
import dayjs from 'dayjs';
import { BookOutlined, EditOutlined, EyeOutlined, FormOutlined, SettingOutlined, UserAddOutlined } from '@ant-design/icons';
import CompanyDisplay from '../CompanyDisplay/CompanyDisplay';
import wheelIcon from "../../../../assets/icons/wheel-icon.svg"
import { colors } from '../../../../utils/colors/colors';
import UpdateClientsDrawer from '../UpdateClientsDrawer/UpdateClientsDrawer';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import { CompanyListData } from '../../../../redux/reducers/clients/clients.types';
import { useNavigate } from 'react-router-dom';
import { Key } from 'antd/es/table/interface';
import { ClientTableData } from '../../../../interfaces/client.interface';

const { useBreakpoint } = Grid;

const ClientsTable = () => {
    const { t } = useTranslation('clients');
    const navigate = useNavigate();

    const screens = useBreakpoint();
    const { companyLists: { data, loading } } = useSelector((state: RootState) => state.clients);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState<string>();
    const processedData = data?.map(item => {
        const { children, ...rest } = item;
        return {
            ...rest,
            relatedCompanies: children
        };
    });

    const truncateText = (text: string, maxLength: number = 18) => {
        if (!text) return "-";
        if (text.length <= maxLength) {
            return <span className="base-table-text">{text}</span>;
        }

        const truncated = text.slice(0, maxLength) + "...";
        return (
            <Tooltip
                title={text}
                color="white"
                overlayInnerStyle={{ color: colors.blueitNavyBlue }}
            >
                <span className="base-table-text" style={{ cursor: 'pointer' }}>
                    {truncated}
                </span>
            </Tooltip>
        );
    };

    const renderStatus = (status: boolean) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: status ? colors.blueitGreen : colors.blueitRed
            }} />
            <span className="base-table-text">{status ? t("active") : t("passive")}</span>
        </div>
    );

    const handleEditClient = (record: CompanyListData) => {
        setSelectedClientId(record.id);
        setDrawerOpen(true);
    };

    const handleAddUser = (record: any) => {
        navigate(`/clients/add-user/${record.id}`);
    };

    const handleViewUsers = (record: any) => {
        navigate(`/clients/view-users/${record.id}`);
    };
    const handleDataWareHouse = (record: any) => {
        navigate(`/clients/data-warehouse/${record.id}`);
    };

    const columns = [
        {
            title: <span className="base-table-text">{t("company_name")}</span>,
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => truncateText(text, 18),
            filters: Array.from(new Set(processedData?.map(item => item.name) || []))
                .map(name => ({ text: name, value: name })),
            filterSearch: true,
            onFilter: (value: boolean | Key, record: ClientTableData) => record.name === value,
        },
        {
            title: <span className="base-table-text">{t("email")}</span>,
            dataIndex: 'email',
            key: 'email',
            render: (text: string) => truncateText(text, 18)
        },
        {
            title: <span className="base-table-text">{t("status")}</span>,
            dataIndex: 'isActive',
            key: 'isActive',
            filters: [
                { text: t("active"), value: true },
                { text: t("passive"), value: false }
            ],
            onFilter: (value: boolean | Key, record: ClientTableData) => record.isActive === value,
            render: (status: boolean) => renderStatus(status)
        },
        {
            title: <span className="base-table-text">{t("accountCreatedDate")}</span>,
            dataIndex: 'accountCreatedDate',
            key: 'accountCreatedDate',
            render: (date: string) => <span className="base-table-text">{dayjs(date).format("DD.MM.YYYY")}</span>
        },
        {
            title: <span className="base-table-text">{t("userCount")}</span>,
            dataIndex: 'userCount',
            key: 'userCount',
            render: (count: number) => <span className="base-table-text">{count ? count : 0}</span>
        },
        {
            title: <span className="base-table-text">{t("relatedCompanies")}</span>,
            dataIndex: 'relatedCompanies',
            key: 'relatedCompanies',
            render: (text: string[]) => <CompanyDisplay companies={text} />,
        },
        {
            title: <span className="base-table-text">{t("userActions")}</span>,
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as const,
            render: (actions: string, record: any) => {
                return (
                    <Row justify={"center"}>
                        <Col xs={6}>
                            <Tooltip
                                placement="topLeft"
                                color='white'
                                title={t("edit")}
                                overlayInnerStyle={{ color: colors.blueitNavyBlue }}
                                getPopupContainer={trigger => trigger.parentElement as HTMLElement}
                            >
                                <Button
                                    type="text"
                                    icon={<FormOutlined style={{ color: "#00000073", fontSize: screens.xxl ? 24 : screens.xl ? 18 : 16 }} />}
                                    onClick={() => handleEditClient(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col xs={6}>
                            <Tooltip
                                placement="topLeft"
                                color='white'
                                title={t("add_user")}
                                overlayInnerStyle={{ color: colors.blueitNavyBlue }}
                                getPopupContainer={trigger => trigger.parentElement as HTMLElement}
                            >
                                <Button
                                    type="text"
                                    icon={<UserAddOutlined style={{ color: "#00000073", fontSize: screens.xxl ? 24 : screens.xl ? 18 : 16 }} />}
                                    onClick={() => handleAddUser(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col xs={6}>
                            <Tooltip
                                placement="topLeft"
                                color='white'
                                title={t("view_users")}
                                overlayInnerStyle={{ color: colors.blueitNavyBlue }}
                                getPopupContainer={trigger => trigger.parentElement as HTMLElement}
                            >
                                <Button
                                    type="text"
                                    icon={<EyeOutlined style={{ color: "#00000073", fontSize: screens.xxl ? 24 : screens.xl ? 18 : 16 }} />}
                                    onClick={() => handleViewUsers(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col xs={6}>
                            <Tooltip
                                placement="topLeft"
                                color='white'
                                title={t("data_warehouse")}
                                overlayInnerStyle={{ color: colors.blueitNavyBlue }}
                                getPopupContainer={trigger => trigger.parentElement as HTMLElement}
                            >
                                <Button
                                    type="text"
                                    icon={<BookOutlined style={{ color: "#00000073", fontSize: screens.xxl ? 24 : screens.xl ? 18 : 16 }} />}
                                    onClick={() => handleDataWareHouse(record)}
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                )
            }
        }
    ];

    return (
        <>
            <Row>
                <Col style={{ overflow: "visible", overflowX: "auto" }} xs={24}>
                    <AppTable
                        className='base-table-body'
                        loading={loading}
                        dataSource={processedData}
                        columns={columns}
                        paginationConfig={{
                            pageSize: 10,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} ${t("record")}`
                        }}
                    />
                </Col>
            </Row>

            <UpdateClientsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                clientId={selectedClientId}
            />
        </>
    )
}

export default ClientsTable